import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardSignature, Plus, FileDown, CheckCheck, Ban } from "lucide-react";
import { toast } from "sonner";
import { onErr, downloadFile, inr, num } from "@/lib/providers";

const TDS_OPTIONS = [
  { value: "194C-1", section: "194C", rate: 0.01, label: "194C – Contract work, individual/HUF (1%)" },
  { value: "194C-2", section: "194C", rate: 0.02, label: "194C – Contract work, firm/company (2%)" },
  { value: "194J-10", section: "194J", rate: 0.10, label: "194J – Professional fees (10%)" },
  { value: "194J-2", section: "194J", rate: 0.02, label: "194J – Technical services (2%)" },
  { value: "NONE", section: "NONE", rate: 0, label: "No TDS (below threshold — confirm with CA)" },
];

const empty = {
  provider_id: "", project_id: "", scope_of_work: "", target_units: "", contract_value: "", payment_mode: "pro_rata",
  milestones: "25, 50, 75, 100", tds: "194C-1", gst_rate: 0.18, start_date: "", end_date: "", assigned_area: "",
};

export default function WorkOrdersTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [statusFor, setStatusFor] = useState(null);
  const [reason, setReason] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const { data: wos, isLoading } = useQuery({ queryKey: ["workOrders"], queryFn: async () => (await api.get("/work-orders")).data });
  const { data: providers } = useQuery({ queryKey: ["providers"], queryFn: async () => (await api.get("/service-providers")).data });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const readyProviders = (providers || []).filter((p) => p.status === "Active");
  const activeProjects = (projects || []).filter((p) => p.status === "Active");
  const chosenProvider = readyProviders.find((p) => p.id === f.provider_id);
  const chosenProject = activeProjects.find((p) => p.id === f.project_id);
  const rate = Number(f.contract_value) > 0 && Number(f.target_units) > 0 ? Number(f.contract_value) / Number(f.target_units) : null;

  const create = useMutation({
    mutationFn: async () => {
      const t = TDS_OPTIONS.find((o) => o.value === f.tds);
      return (await api.post("/work-orders", {
        provider_id: f.provider_id, project_id: f.project_id, scope_of_work: f.scope_of_work,
        target_units: Number(f.target_units), contract_value: Number(f.contract_value), payment_mode: f.payment_mode,
        milestones: f.payment_mode === "milestone" ? f.milestones.split(",").map((x) => Number(x.trim())).filter((x) => x) : [],
        tds_section: t.section, tds_rate: t.rate, gst_rate: Number(f.gst_rate), start_date: f.start_date, end_date: f.end_date,
        assigned_area: f.assigned_area,
      })).data;
    },
    onSuccess: (d) => { toast.success(`Work order ${d.wo_number} issued`); qc.invalidateQueries({ queryKey: ["workOrders"] }); setOpen(false); setF(empty); },
    onError: onErr,
  });

  const setStatus = useMutation({
    mutationFn: async () => (await api.put(`/work-orders/${statusFor.wo.id}/status`, { status: statusFor.status, reason })).data,
    onSuccess: () => { toast.success("Work order updated"); qc.invalidateQueries({ queryKey: ["workOrders"] }); setStatusFor(null); setReason(""); },
    onError: onErr,
  });

  const valid = f.provider_id && f.project_id && f.scope_of_work.length >= 5 && Number(f.target_units) > 0 && Number(f.contract_value) > 0 && f.start_date && f.end_date;

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3">
        <p className="text-sm text-slate-500">A work order fixes the deliverable, the fee and the payment basis. Providers are paid only for verified output.</p>
        <Button className="bg-navy hover:bg-navy-700 shrink-0" onClick={() => { setF(empty); setOpen(true); }} data-testid="add-wo"><Plus className="h-4 w-4 mr-1" /> Issue Work Order</Button>
      </div>
      {isLoading ? <Loading /> : !wos?.length ? (
        <EmptyState icon={ClipboardSignature} title="No work orders yet" hint="Create a project and onboard a provider first." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["WO", "Provider", "Project", "Deliverable", "Value", "Progress", "Billed", "Status", ""].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {wos.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="font-mono text-sm text-teal">{w.wo_number}</TableCell>
                  <TableCell className="font-medium text-navy">{w.provider_name}<div className="text-xs text-slate-400">{w.provider_code}</div></TableCell>
                  <TableCell className="text-sm">{w.project_name}<div className="text-xs text-slate-400">{w.assigned_area}</div></TableCell>
                  <TableCell className="text-sm">{num(w.target_units)} {w.unit_label?.toLowerCase()}<div className="text-xs text-slate-400">{w.payment_mode === "pro_rata" ? `${inr(w.unit_rate)} / unit` : `Milestones ${w.milestones.join("/")}%`}</div></TableCell>
                  <TableCell className="text-sm">{inr(w.contract_value)}<div className="text-xs text-slate-400">TDS {w.tds_section} {w.tds_rate * 100}%</div></TableCell>
                  <TableCell className="min-w-[140px]">
                    <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-teal" style={{ width: `${Math.min(100, w.progress.percent)}%` }} /></div>
                    <div className="text-xs text-slate-500 mt-1">{num(w.progress.verified_units)} verified · {w.progress.percent}%{w.progress.pending_logs ? ` · ${w.progress.pending_logs} to verify` : ""}</div>
                  </TableCell>
                  <TableCell className="text-sm">{inr(w.progress.billed_value)}</TableCell>
                  <TableCell><StatusBadge status={w.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" title="Download work order PDF" onClick={() => downloadFile(`/work-orders/${w.id}/pdf`, `WorkOrder_${w.wo_number}.pdf`)}><FileDown className="h-4 w-4" /></Button>
                    {w.status === "Active" && <>
                      <Button size="sm" variant="ghost" title="Mark completed" onClick={() => setStatusFor({ wo: w, status: "Completed" })}><CheckCheck className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" title="Cancel" onClick={() => setStatusFor({ wo: w, status: "Cancelled" })}><Ban className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Issue Work Order</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Service provider *</Label>
              <Select value={f.provider_id} onValueChange={(v) => set("provider_id", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Choose provider" /></SelectTrigger>
                <SelectContent className="bg-white">{readyProviders.map((p) => <SelectItem key={p.id} value={p.id}>{p.provider_code} · {p.name}</SelectItem>)}</SelectContent>
              </Select>
              {chosenProvider?.onboarding_gaps?.length > 0 && <p className="text-xs text-amber-700 mt-1">Onboarding incomplete: {chosenProvider.onboarding_gaps.join("; ")}</p>}
            </div>
            <div>
              <Label>Project *</Label>
              <Select value={f.project_id} onValueChange={(v) => {
                const p = activeProjects.find((x) => x.id === v);
                setF((x) => ({ ...x, project_id: v, start_date: x.start_date || p?.start_date || "", end_date: x.end_date || p?.end_date || "" }));
              }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Choose project" /></SelectTrigger>
                <SelectContent className="bg-white">{activeProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Scope of work / deliverable *</Label><Textarea className="mt-1" rows={3} value={f.scope_of_work} onChange={(e) => set("scope_of_work", e.target.value)} placeholder="Door-to-door symptom screening of 4,000 population in the allotted wards as per NTEP ACF protocol, with referral of presumptive TB cases." /></div>
            <div><Label>Target ({chosenProject?.unit_label || "units"}) *</Label><Input type="number" className="mt-1" value={f.target_units} onChange={(e) => set("target_units", e.target.value)} placeholder="4000" /></div>
            <div><Label>Contract value, excl. GST (₹) *</Label><Input type="number" className="mt-1" value={f.contract_value} onChange={(e) => set("contract_value", e.target.value)} placeholder="100000" /></div>
            <div>
              <Label>Payment basis</Label>
              <Select value={f.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="pro_rata">Pro-rata per verified unit</SelectItem>
                  <SelectItem value="milestone">Milestones (% of target)</SelectItem>
                </SelectContent>
              </Select>
              {f.payment_mode === "pro_rata" && rate && <p className="text-xs text-teal mt-1">= {inr(rate)} per verified unit</p>}
            </div>
            {f.payment_mode === "milestone" && <div><Label>Milestones (cumulative %)</Label><Input className="mt-1" value={f.milestones} onChange={(e) => set("milestones", e.target.value)} /></div>}
            <div className={f.payment_mode === "milestone" ? "sm:col-span-2" : ""}>
              <Label>TDS</Label>
              <Select value={f.tds} onValueChange={(v) => set("tds", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{TDS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">No PAN on file → 20% is applied automatically (Sec 206AA).</p>
            </div>
            <div>
              <Label>GST rate (only if provider is registered)</Label>
              <Select value={String(f.gst_rate)} onValueChange={(v) => set("gst_rate", Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{[0, 0.05, 0.12, 0.18].map((r) => <SelectItem key={r} value={String(r)}>{r * 100}%</SelectItem>)}</SelectContent>
              </Select>
              {chosenProvider && <p className="text-xs text-slate-400 mt-1">{chosenProvider.gst_registered ? `GST will be added (GSTIN ${chosenProvider.gstin})` : "Provider not GST-registered — no GST will be added"}</p>}
            </div>
            <div><Label>Start date *</Label><Input type="date" className="mt-1" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div><Label>End date *</Label><Input type="date" className="mt-1" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Assigned area (wards / zones)</Label><Input className="mt-1" value={f.assigned_area} onChange={(e) => set("assigned_area", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!valid || create.isPending} onClick={() => create.mutate()}>Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusFor} onOpenChange={(o) => !o && setStatusFor(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>{statusFor?.status === "Cancelled" ? "Cancel" : "Complete"} {statusFor?.wo.wo_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">The provider can no longer submit logs on this work order. Verified output already logged can still be paid.</p>
          <div><Label>Reason {statusFor?.status === "Cancelled" ? "*" : "(optional)"}</Label><Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusFor(null)}>Back</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={(statusFor?.status === "Cancelled" && !reason) || setStatus.isPending} onClick={() => setStatus.mutate()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
